import { useEffect } from 'react'

import { z } from 'zod'

import { cn } from '@fortem-monorepo/js-utilities'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormDescription,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  NumberInput,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { usePnlContext } from '@/pages/pnl-attributions/context'
import { useAdjustTradeMutation } from '@/pages/pnl-attributions/mutations'
import { formatNumber } from '@/utilities/format'

export interface AdjustTradeDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    original_pnl: number
    trade_id: string
    attribution_id: number
    batch_id?: number | null
  }
}

const adjustTradeSchema = z.object({
  target_net_pnl: z.coerce.number(),
  reason: z.string().min(1, 'You must provide a reason for adjusting the PnL'),
  ticket_reference: z.string().optional().nullable(),
})

export const AdjustTradeDialog: React.FC<AdjustTradeDialogProps> = ({ mutationParams, ...props }) => {
  const { params } = usePnlContext()

  const adjustTradeMutation = useAdjustTradeMutation({
    fund_name: params.fund_name,
    value_date: params.value_date,
  })

  const form = useFormWithSchema({ mode: 'all' }, adjustTradeSchema)

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await adjustTradeMutation.mutateAsync({
      ...data,
      attribution_id: mutationParams.attribution_id,
      batch_id: mutationParams.batch_id,
    })
    props.onClose?.()
  })

  const target_net_pnl = form.watch('target_net_pnl')
  const diff = target_net_pnl ? target_net_pnl - mutationParams.original_pnl : 0

  return (
    <ConfirmationDialog
      {...props}
      action={onSubmit}
      title={`Adjust PnL for trade ${mutationParams.trade_id}`}
      description={`This will override the P&L for this trade.`}
      valid={form.formState.isValid}
      pending={adjustTradeMutation.isPending}
    >
      <div className="flex items-center gap-10">
        <div className="flex flex-col gap-1 pb-4">
          <span className="font-medium uppercase text-muted-foreground text-xs">Original PnL</span>
          <span className="font-mono text-sm">{formatNumber(mutationParams.original_pnl)}</span>
        </div>
        <div className="flex flex-col gap-1 pb-4">
          <span className="font-medium uppercase text-muted-foreground text-xs">Diff</span>
          <span className="font-mono text-sm">
            <div
              className={cn('flex items-center gap-px', {
                'text-emerald-700': diff > 0,
                'text-red-700': diff < 0,
              })}
            >
              {diff > 0 && <span>+</span>}
              <span className="font-mono text-sm">{formatNumber(diff)}</span>
            </div>
          </span>
        </div>
      </div>
      <Form {...form}>
        <div className="space-y-4">
          <FormFieldRow>
            <FormField
              control={form.control}
              name="target_net_pnl"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>New P&L</FormLabel>
                      <FormError />
                    </div>
                    <NumberInput
                      thousandSeparator={','}
                      value={field.value}
                      onValueChange={(values) => field.onChange(values.floatValue)}
                    />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Reason</FormLabel>
                      <FormError />
                    </div>
                    <FormDescription>Provide a reason for the adjustment.</FormDescription>
                    <Textarea value={field.value} onChange={field.onChange} />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="ticket_reference"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Ticket Reference</FormLabel>
                      <FormError />
                    </div>
                    <FormDescription>Provide an optional reference for this adjustment.</FormDescription>
                    <Textarea value={field.value ?? ''} onChange={field.onChange} />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
        </div>
      </Form>
    </ConfirmationDialog>
  )
}
